import { getType, getRender } from '~/scripts/constants/mime';
import APIV5 from '~/models/api';
import { MAX_PREVIEW_SIZE } from '~/pages/chat/chat.constant';
import { calculateAspectRatioFit } from '~/utils/display';
import { BaseController } from '~/utils/base-controller';

/**
 * @implements {FileViewer.Controller}
 */
// @ts-ignore
export default class FileViewerController extends BaseController {
	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 */
	constructor(
		$injector,
	) {
		super($injector);
		this.$http = $injector.get('$http');

		this.buffer = undefined;

		/** @type {boolean} */
		this.bypassSize = false;

		/** @type {string} */
		this.filename = 'Unknown filename';

		/** @type {?import('~/scripts/services/model/form').default} */
		this.form = null;

		/** @type {?number} */
		this.height = null;

		/** @type {boolean} */
		this.hideCaption = false;

		/** @type {string} */
		this.id = '';

		/** @type {File | any} */
		this.infos = null;

		/** @type {boolean} */
		this.isLoading = true;

		/** @type {?import('@knitiv/api-client-javascript').KNode} */
		this.kid = null;

		/** @type {string} */
		this.mimetype = '';

		/** @type {string} */
		this.module = 'default';

		/** @type {?Function} */
		this.onError = null;

		/** @type {number} */
		this.progress = 0;

		/** @type {string} */
		this.render = '';

		/** @type {number} */
		this.size = 0;

		this.style = {
			height: '100%',
			width: '100%',
		};

		/** @type {boolean} */
		this.tooHeavy = false;

		this.url = undefined;

		this.viewer = undefined;

		/** @type {?number} */
		this.width = null;
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		if (changes.height || changes.width) {
			this.style = { height: `${this.height}`, width: `${this.width}` };
		}
	}

	/**
	 *
	 * @param {{ target: HTMLImageElement }} event
	 */
	onImageLoad(event) {
		const image = event.target;

		const { naturalHeight } = image;
		const { naturalWidth } = image;

		const newSize = calculateAspectRatioFit(naturalWidth, naturalHeight, 400, 300);
		// eslint-disable-next-line unicorn/consistent-destructuring
		image.style.height = `${newSize.height}px`;
		// eslint-disable-next-line unicorn/consistent-destructuring
		image.style.width = `${newSize.width}px`;
	}

	/**
	 *
	 * @param {string} source
	 * @returns {any}
	 */
	trustSrc(source) {
		return typeof source === 'string' ? this.$sce.trustAsResourceUrl(source) : source;
	}

	async reloadAndBypass() {
		this.bypassSize = true;
		await this.loadFile();
		this.$timeout(() => this.$rootScope.$apply());
	}

	async loadFile() {
		this.isLoading = this.viewer === 'loading';

		if (this.kid) {
			this.isLoading = true;

			try {
				if (!this.infos) {
					if (this.$rootScope.kds) {
						const resultInfosMultiple = await APIV5.instance.fileInfoMultiple({
							nodeid: this.kid,
							id:     this.id === '' ? undefined : this.id,
							type:   'viewer',
						}, {
							id: 'file-viewer.load-file',
						});
						if (!this.id && resultInfosMultiple.files.length > 0) {
							this.id = resultInfosMultiple.files[0].id;
						}
						this.infos = resultInfosMultiple.files?.find(f => f.id === this.id);
					}
					else {
						this.infos = await APIV5.instance.fileInfo({
							nodeid: this.kid,
							type:   'viewer',
						}, {
							id: 'file-viewer.load-file',
						});
					}
				}

				if (this.infos) {
					this.filename = this.infos.filename ?? this.infos.name;
					this.mimetype = this.infos.mimetype ?? this.infos.type;

					const viewer = getType(this.mimetype);
					let render = getRender(viewer);

					// force inline mode if user just dropped a file
					if (['video', 'audio', 'image', 'pdf'].includes(viewer) && this.infos instanceof File) {
						render = 'inline';
					}

					this.isLoading = false;
					this.render = render;
					this.viewer = viewer;
					this.size = this.infos.size;
					this.tooHeavy = false;

					if (this.infos.size > MAX_PREVIEW_SIZE && this.bypassSize === false) {
						this.render = 'none';
						this.tooHeavy = true;
					}
					else {
						/** @type {import('@knitiv/api-client-javascript').DownloadInput} */
						const downloadRequest = {
							inline: true,
							mode:   'chunked',
							type:   'viewer',
						};

						/** @type {import('@knitiv/api-client-javascript').RqOptions} */
						const options = {
							id: 'file-viewer.load-file',
						}

						if (this.$rootScope.kds && this.id) {
							downloadRequest.id = this.id;
							options.endpointVersion = 3;
						}
						else {
							downloadRequest.nodeid = this.kid;
						}

						const pdfPrepend = '#view=FitH'; // '#zoom=FitW'

						if (render === 'inline') {
							let url;

							if (this.infos instanceof File) {
								url = this.$sce.trustAsResourceUrl(
									URL.createObjectURL(new Blob([this.infos], { type: this.infos.type })),
								);
							}
							else if (this.infos.provider?.name === 'opentext' && this.form) {
								const repre = this.form?.getRepre('K_NODE;OPENTEXTID');
								if (repre) {
									const result = await this.$http.get(
										`${this.infos.provider.url}/api/v2/nodes/${repre.value}/content`,
										{
											headers: {
												OTCSTicket: this.infos.provider.ticket,
											},
											responseType: 'arraybuffer',
										}
									);
									if (result) {
										// const arrayBuffer = new ArrayBuffer(result.data.length);
										// const view = new Uint8Array(arrayBuffer);
										// for (let index = 0 ; index < result.data.length ; index++) {
										// 	view[index] = result.data.codePointAt(index);
										// }
										// this.text = new TextDecoder('utf8').decode(result.data);

										url = URL.createObjectURL(
											new Blob([result.data], { type: result.headers('content-type') }),
										);
										// url = result.data;
									}
								}
							}

							if (!url) {
								url = APIV5.instance.downloadURL(downloadRequest, options);
							}

							if (this.viewer === 'pdf') {
								url += pdfPrepend;
							}

							this.url = this.trustSrc(url);

							this.$timeout(() => {
								this.$rootScope.$apply();
							});
						}
						else if (render === 'custom') {
							const dl = await APIV5.instance.download(downloadRequest, options);

							this.$timeout(() => {
								this.buffer = dl.buffer;

								this.text = new TextDecoder('utf8').decode(this.buffer);

								let url = URL.createObjectURL(
									new Blob([this.buffer], { type: this.infos?.mimetype }),
								);
								if (this.viewer === 'pdf') {
									url += pdfPrepend;
								}
								this.url = this.trustSrc(url);

								this.$timeout(() => {
									this.$rootScope.$apply();
								});
							});
						}
					}
				}
			}
			catch (/** @type {any} */ error) {
				this.isLoading = false;
				this.viewer = 'error';
				if (this.onError) {
					this.onError({ error, kid: this.kid });
				}
				console.error(error);
			}
		}
	}

	download() {
		this.FileDownloadService
			.download([{ kid: this.kid }])
			.catch(error => {
				console.error(error);
			});
	}

	// eslint-disable-next-line consistent-return
	async $onInit() {
		await this.loadFile();
	}

	$doCheck() {}

	$onDestroy() {}

	$postLink() {}
}
